<template>
  <v-layout column :class="{ 'accreditation-form--create-layout': createLayout }">
    <template v-if="createLayout">
      <v-flex v-if="avatarField" class="accreditation-form__field">
        <component
          :is="avatarField.component"
          v-model="form[avatarField.key]"
          :validator="vs && vs.form[avatarField.key]"
          v-bind="{ ...workflowUi(avatarField), label: workflowSchemaFieldLabel(avatarField) }"
        />
      </v-flex>

      <v-layout
        v-if="createNameFields.length"
        :column="$vuetify.breakpoint.xs"
        class="accreditation-form__name-row"
      >
        <v-flex
          v-for="field in createNameFields"
          :key="`name-${field.key}`"
          class="accreditation-form__name-field"
        >
          <component
            :is="field.component"
            v-model="form[field.key]"
            :validator="vs && vs.form[field.key]"
            v-bind="{ ...workflowUi(field), label: workflowSchemaFieldLabel(field) }"
            :error-messages="leadFieldErrorMessages(field)"
            @input="onLeadFieldInput(field)"
          />
        </v-flex>
      </v-layout>

      <v-flex
        v-for="field in createLeadFields"
        :key="`lead-${field.key}`"
        class="accreditation-form__field"
      >
        <v-combobox
          v-if="isCompanyField(field)"
          ref="companyComboboxField"
          v-model="form[field.key]"
          v-bind="{ ...workflowUi(field), label: workflowSchemaFieldLabel(field) }"
          :error-messages="getErrors(vs.form[field.key])"
          :items="workflowUi(suggestedCompanies)"
          :search-input.sync="form[field.key]"
          item-text="name"
          item-value="name"
          box
          append-icon=""
          hide-no-data
          :loading="suggestionsLoading"
          @change="populateSuggested"
          @blur="onCompanySelectBlur"
          @input="onCompanyInputChange"
        />
        <component
          :is="field.component"
          v-else
          v-model="form[field.key]"
          :validator="vs && vs.form[field.key]"
          v-bind="{ ...workflowUi(field), label: workflowSchemaFieldLabel(field) }"
          :error-messages="leadFieldErrorMessages(field)"
          @input="onLeadFieldInput(field)"
        />
      </v-flex>

      <v-flex
        v-if="!category && event.extraModules.categoriesAndZones"
        class="accreditation-form__field"
      >
        <select-field
          :value="form.category"
          :items="workflowUi(event.categories)"
          :validator="vs.form.category"
          :label="workflowLabel(styles.category || adminText('workflowsAll.category_a3c686e', 'Category'))"
          item-render="name"
          item-select="id"
          @change="onCategoryChange"
        />
      </v-flex>
      <v-flex
        v-if="event.extraModules.categoriesAndZones && event.numberOfZones > 0"
        v-show="form.category"
        class="accreditation-form__field"
      >
        <zone-picker-field
          v-model="form.zones"
          :number-of-zones="event.numberOfZones"
          :validator="vs.form.zones"
          :label="workflowLabel(styles.zones || adminText('workflowsAll.zones_182e2eb', 'Zones'))"
        />
      </v-flex>

      <v-flex
        v-for="field in createOtherFields"
        :key="`other-${field.key}`"
        class="accreditation-form__field"
      >
        <div v-if="['Images', 'Files'].includes(field.key)">
          <div v-if="form[field.key] && form[field.key].length > 0">
            <label class="v-label theme--light">{{ workflowLabel(field.label) }}</label>
            <div v-for="(url, index) in form[field.key]" :key="index" class="mt-2">
              {{ adminText('workflowsAll.download_d41afcd', 'Download:') }}
              <a :href="url" target="_blank" download>
                {{ workflowLabel((url || '').split('/').pop()) }}
              </a>
            </div>
          </div>
        </div>
        <component
          :is="field.component"
          v-else
          v-model="form[field.key]"
          :validator="vs && vs.form[field.key]"
          v-bind="{ ...workflowUi(field), label: workflowSchemaFieldLabel(field) }"
          :error-messages="otherFieldErrorMessages(field)"
          @input="onOtherFieldInput(field)"
        />
      </v-flex>
    </template>

    <template v-else>
      <v-layout :column="$vuetify.breakpoint.xs">
        <v-flex
          v-if="attendeeFields[0] && attendeeFields[0].component === 'avatar-field'"
          shrink
          class="mr-16"
        >
          <v-flex v-for="field in firstField" :key="field.key">
            <component
              :is="field.component"
              v-model="form[field.key]"
              :validator="vs && vs.form[field.key]"
              v-bind="{ ...workflowUi(field), label: workflowSchemaFieldLabel(field) }"
            />
          </v-flex>
        </v-flex>
        <v-flex>
          <v-layout column>
            <v-flex v-for="field in firstThreeFields" :key="field.key" class="pb-16">
              <v-combobox
                v-if="isCompanyField(field)"
                ref="companyComboboxField"
                v-model="form[field.key]"
                v-bind="{ ...workflowUi(field), label: workflowSchemaFieldLabel(field) }"
                :error-messages="getErrors(vs.form[field.key])"
                :items="workflowUi(suggestedCompanies)"
                :search-input.sync="form[field.key]"
                item-text="name"
                item-value="name"
                box
                append-icon=""
                hide-no-data
                :loading="suggestionsLoading"
                @change="populateSuggested"
                @blur="onCompanySelectBlur"
                @input="onCompanyInputChange"
              />
              <component
                :is="field.component"
                v-else
                v-model="form[field.key]"
                :validator="vs && vs.form[field.key]"
                v-bind="{ ...workflowUi(field), label: workflowSchemaFieldLabel(field) }"
                :error-messages="leadFieldErrorMessages(field)"
                @input="onLeadFieldInput(field)"
              />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-layout column>
        <v-flex v-if="!category && event.extraModules.categoriesAndZones" class="pb-16">
          <select-field
            :value="form.category"
            :items="workflowUi(event.categories)"
            :validator="vs.form.category"
            :label="workflowLabel(styles.category || adminText('workflowsAll.category_a3c686e', 'Category'))"
            item-render="name"
            item-select="id"
            @change="onCategoryChange"
          />
        </v-flex>
        <v-flex
          v-if="event.extraModules.categoriesAndZones && event.numberOfZones > 0"
          v-show="form.category"
          class="pb-16"
        >
          <zone-picker-field
            v-model="form.zones"
            :number-of-zones="event.numberOfZones"
            :validator="vs.form.zones"
            :label="workflowLabel(styles.zones || adminText('workflowsAll.zones_182e2eb', 'Zones'))"
          />
        </v-flex>
        <v-flex v-for="field in otherFields" :key="field.key" class="pb-16">
          <div v-if="['Images', 'Files'].includes(field.key)">
            <div v-if="form[field.key] && form[field.key].length > 0">
              <label class="v-label theme--light">{{ workflowLabel(field.label) }}</label>
              <div v-for="(url, index) in form[field.key]" :key="index" class="mt-2">
                {{ adminText('workflowsAll.download_d41afcd', 'Download:') }}
                <a :href="url" target="_blank" download>
                  {{ workflowLabel((url || '').split('/').pop()) }}
                </a>
              </div>
            </div>
          </div>
          <component
            :is="field.component"
            v-else
            v-model="form[field.key]"
            :validator="vs && vs.form[field.key]"
            v-bind="{ ...workflowUi(field), label: workflowSchemaFieldLabel(field) }"
            :error-messages="otherFieldErrorMessages(field)"
            @input="onOtherFieldInput(field)"
          />
        </v-flex>
      </v-layout>
    </template>
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { SEARCH_EXPO_COMPANIES } from '@/graphql/expoCompany.graphql'
import { defaultMessages, getValidationErrors } from '@/utils'

export default {
  mixins: [WorkflowDisplay],
  name: 'accreditation-form',
  props: {
    event: {
      type: Object,
      default: null,
    },
    category: {
      type: Object,
      default: () => {},
    },
    initialValues: {
      type: Object,
      default: () => {},
    },
    vs: {
      type: Object,
      default: () => {},
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    publicForm: {
      type: Boolean,
      default: true,
    },
    createLayout: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {},
      attendeeFields: this.fieldOrder(),
      suggestedCompanies: [],
      requestInProgress: undefined,
      suggestionsLoading: false,
    }
  },
  computed: {
    avatarField() {
      return this.attendeeFields.find(field => field.component === 'avatar-field')
    },
    createNameFields() {
      return ['First name', 'Last name']
        .map(key => this.attendeeFields.find(field => field.key === key))
        .filter(Boolean)
    },
    createLeadFields() {
      return this.firstThreeFields.filter(field => !this.isNameField(field))
    },
    createOtherFields() {
      return this.otherFields.filter(field => !this.isNameField(field))
    },
    firstField() {
      return this.attendeeFields.slice(0, 1)
    },
    firstThreeFields() {
      if (this.attendeeFields[0] && this.attendeeFields[0].component === 'avatar-field') {
        return this.attendeeFields.slice(1, 4)
      } else {
        return this.attendeeFields.slice(0, 4)
      }
    },
    otherFields() {
      return this.attendeeFields.slice(4)
    },
  },
  watch: {
    fields: {
      handler() {
        // Keep rendered definitions in step with live validators without remounting
        // the form or replacing any values already entered by the attendee.
        this.attendeeFields = this.fieldOrder()
      },
      deep: true,
    },
    form: {
      handler(val) {
        this.$emit('values', val)
      },
      deep: true,
    },
    '$i18next.resolvedLanguage': function () {
      const lng = this.$i18next.resolvedLanguage || this.$i18next.language
      const ns = 'default'

      this.attendeeFields.forEach(val => {
        let translatedKey = this.$i18next.getResource(lng, ns, val.key)

        if (translatedKey) {
          val.label = translatedKey
        } else {
          val.label = val.key
        }
      })
    },
  },

  mounted() {
    const initial = { zones: [], ...this.initialValues }
    this.form = Object.entries(this.$route.query).reduce((acc, [key, value]) => {
      const replaced = key.split('_').join(' ')
      if (!value || !this.fields.find(f => f.key === replaced)) return acc
      acc[replaced] = value

      return acc
    }, initial)
  },
  methods: {
    isCroatianRequiredEvent() {
      return this.event && [2444, 4531, 8265].includes(Number(this.event.id))
    },
    isNameField(field) {
      return ['First name', 'Last name'].includes(field.key)
    },
    isCompanyField(field) {
      return (
        this.event &&
        this.event.extraModules &&
        this.event.extraModules.expoMobileApp &&
        field.key === 'Naziv tvrtke / Company name'
      )
    },
    leadFieldErrorMessages(field) {
      return {
        ...this.adminFieldValidationMessages(field.errorMessages),
        required: this.isCroatianRequiredEvent()
          ? this.adminText('workflowsAll.ovo_polje_je_obavezno_e25488b', 'Ovo polje je obavezno')
          : this.adminText('workflowsAll.this_field_is_required_8aa9bf0', 'This field is required'),
      }
    },
    otherFieldErrorMessages(field) {
      return {
        ...this.adminFieldValidationMessages(field.errorMessages),
        email: this.isCroatianRequiredEvent()
          ? this.adminText('workflowsAll.molimo_obavezno_navedite_email_0c30239', 'Molimo obavezno navedite email')
          : this.adminText('workflowsAll.this_field_must_be_an_email_ededd72', 'This field must be an email'),
        required: this.isCroatianRequiredEvent()
          ? this.adminText('workflowsAll.ovo_polje_je_obavezno_e25488b', 'Ovo polje je obavezno')
          : this.adminText('workflowsAll.this_field_is_required_8aa9bf0', 'This field is required'),
        maxLength:
          this.isCroatianRequiredEvent() && field.key == 'Tvrtka/ustanova'
            ? this.adminText('workflowsAll.molimo_skratite_naziv_na_32_znakova_npr_minis_a92b099', 'Molimo skratite naziv na 32 znakova (npr. Ministarstvo regionalnog razvoja i fondova Europske unije upisati kao MRRFEU).')
            : this.adminText('workflowsAll.this_field_should_contain_less_values_f6d7a59', 'This field should contain less values'),
      }
    },
    onLeadFieldInput(field) {
      if (this.isCroatianRequiredEvent() && this.isNameField(field)) {
        this.uppercase(field.key)
      }
    },
    onOtherFieldInput(field) {
      if (this.isCroatianRequiredEvent() && field.key == 'Tvrtka/ustanova') {
        this.uppercase(field.key)
      }
    },
    fieldOrder() {
      let attendeefields = this.fields.filter(item => !item.isPrivate)
      const eventId = this.event.id
      const avatarIndex = attendeefields.findIndex(item => item.component === 'avatar-field')
      if (avatarIndex !== -1) {
        attendeefields.unshift(...attendeefields.splice(avatarIndex, 1))
      }
      attendeefields.forEach(item => {
        if (item.key === 'First name' && [2444, 4531, 8265].includes(Number(eventId)))
          item.label = 'Ime'
        if (item.key === 'Last name' && [2444, 4531, 8265].includes(Number(eventId)))
          item.label = 'Prezime'
      })
      attendeefields = attendeefields
        .filter(
          f =>
            !(
              ['Datum rodenja', 'Datum rođenja'].includes(f.label) &&
              [952, 2444, 4531, 8265].includes(parseInt(eventId))
            ),
        )
        .filter(f => !(f.label === 'OIB' && [2444, 4531,8265].includes(Number(eventId))))
        .filter(f => f.label !== 'entryTo' && f.label !== 'entryFrom')

      if (Number(eventId) === 8265) {
        attendeefields = attendeefields.map(field =>
          field.key === 'Tvrtka/ustanova'
            ? {
              ...field,
              maxLength: 32,
              hint:
                  'workflowsAll.najvise_32_znaka_ukljucujuci_razmake_molimo_s_5e9369e',
              persistentHint: true,
            }
            : field,
        )
      }

      return attendeefields
    },

    async onCategoryChange(categoryId) {
      if (this.form.category === categoryId) return
      const category = this.event.categories.find(({ id }) => id === categoryId)
      if (category) this.form = { ...this.form, zones: category.zones, category: category.id }
    },

    uppercase(field) {
      this.form[field] = this.form[field].toUpperCase()
    },

    getErrors: function (field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },

    async populateSuggested(input) {
      if (!(typeof input === 'string') || !input || !input.trim()) return

      if (this.requestInProgress) clearTimeout(this.requestInProgress)
      this.suggestionsLoading = true
      this.requestInProgress = setTimeout(async () => {
        const {
          data: { searchExpoCompanies },
          errors,
        } = await this.$apollo.query({
          query: SEARCH_EXPO_COMPANIES,
          variables: {
            eventId: this.event.id,
            searchTerm: input,
          },
        })

        if (!errors) {
          this.suggestedCompanies = searchExpoCompanies
        }
        this.suggestionsLoading = false
      }, 500)
    },
    onCompanySelectBlur() {
      if (this.vs.form['Naziv tvrtke / Company name']) {
        this.vs.form['Naziv tvrtke / Company name'].$touch()
      }
      if (this.$refs.companyComboboxField && this.$refs.companyComboboxField.length) {
        this.$refs.companyComboboxField[0].$data.isMenuActive = false
        this.$refs.companyComboboxField[0].$data.isFocused = false
      }
    },
    onCompanyInputChange(value) {
      this.form['OIB / Company VAT number'] = value.OIB
    },
  },
}
</script>

<style scoped>
.accreditation-form--create-layout .accreditation-form__name-row {
  gap: 24px;
  margin-bottom: 32px;
}
.accreditation-form--create-layout .accreditation-form__name-field {
  min-width: 0;
}
.accreditation-form--create-layout .accreditation-form__field {
  margin-bottom: 32px;
}
.accreditation-form--create-layout .accreditation-form__field:last-child {
  margin-bottom: 0;
}
.accreditation-form--create-layout >>> .v-input {
  margin-top: 0;
}
.accreditation-form--create-layout >>> .v-text-field.v-text-field--box .v-input__slot,
.accreditation-form--create-layout >>> .v-select.v-text-field--box .v-input__slot {
  min-height: 56px;
  border-radius: 4px;
}
@media screen and (max-width: 600px) {
  .accreditation-form--create-layout .accreditation-form__name-row {
    gap: 0;
  }
  .accreditation-form--create-layout .accreditation-form__name-field:first-child {
    margin-bottom: 32px;
  }
}
</style>
